import styled from '@emotion/styled';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { TimingSystems } from '@racemap/utilities/consts/events';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import type { StartTimesState } from '@racemap/utilities/types/events';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { Timestamp, TooltipWithUTCDatetime } from 'src/components/BasicComponents';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import type { CurrentEvent } from 'src/store/events/events_reducers';
import useSWR from 'swr';
import { BorderedBox } from '../../BasicComponents/Boxes/Boxes';
import { CheckList, type CheckpointEntry } from '../../BasicComponents/CheckList';
import { HStack, VStack } from '../../BasicComponents/MetaComponent';
import { TimingSystemConfiguration } from '../TimingSystemConfiguration';
import { Checkpoint } from './Checkpoint';
import { ShadowtrackCheckpoint, getShadowtrackCheckpointState } from './ShadowtrackCheckpoint';
import { SpeedCheckpoint, getSpeedCheckpointState } from './SpeedCheckpoint';
import { StatusCheckpoint, getStatusCheckpointStatus } from './StatusCheckpoint';
import { TransponderCheckpoint, getTransponderCheckpointState } from './TransponderCheckpoint';
// import { TransitionsCheckpoint, useTransitionsCheckpointState } from './TransitionsCheckpoint';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const PredictionChecklist: FC = () => {
  const event = useCurrentEvent();
  const isPredictionEnabled = !!event?.modules.predictive.enabled;

  const checkpoints: Array<CheckpointEntry> = [
    getStatusCheckpoint(event),
    getTimingSystemCheckpoint(event),
    getShadowtrackCheckpoint(event),
    getTransponderCheckpoint(event),
    // getTransitionsCheckpoint(event),
    getSpeedCheckpoint(event),
    getStartTimesCheckPoint(event),
  ];

  return (
    <BorderedBox>
      <VStack>
        {isPredictionEnabled && (
          <p>
            The below checklist shows the settings needed for a precise prediction. Take care to
            provide the required information.
          </p>
        )}
        <CheckList checkpoints={checkpoints} />
      </VStack>
    </BorderedBox>
  );
};

function getStatusCheckpoint(event: Immutable<CurrentEvent> | null): CheckpointEntry {
  return {
    status: getStatusCheckpointStatus(event),
    key: 'status',
    checkpoint: <StatusCheckpoint event={event} />,
  };
}

function getTimingSystemCheckpoint(event: Immutable<CurrentEvent> | null): CheckpointEntry {
  return {
    checkpoint: (
      <Checkpoint>
        <TimingSystemConfiguration />
      </Checkpoint>
    ),
    key: 'timingSystem',
    status:
      event != null && event.modules.predictive.timingsystem !== TimingSystems.NONE
        ? 'success'
        : 'secondary',
  };
}

function getSpeedCheckpoint(event: Immutable<CurrentEvent> | null): CheckpointEntry {
  return {
    checkpoint: <SpeedCheckpoint event={event} />,
    key: 'speed',
    status: getSpeedCheckpointState(event),
  };
}

function getShadowtrackCheckpoint(event: Immutable<CurrentEvent> | null): CheckpointEntry {
  return {
    status: getShadowtrackCheckpointState(event),
    key: 'shadowtrack',
    checkpoint: <ShadowtrackCheckpoint event={event} />,
  };
}

function getTransponderCheckpoint(event: Immutable<CurrentEvent> | null): CheckpointEntry {
  return {
    status: getTransponderCheckpointState(event),
    key: 'transponder',
    checkpoint: <TransponderCheckpoint event={event} />,
  };
}

/*
function getTransitionsCheckpoint(event: Immutable<CurrentEvent> | null): CheckpointEntry {
  return {
    checkpoint: <TransitionsCheckpoint event={event} />,
    key: 'transitions',
    status: useTransitionsCheckpointState(event),
  };
}
*/

function getStartTimesCheckPoint(event: Immutable<CurrentEvent> | null): CheckpointEntry {
  if (event == null)
    return {
      checkpoint: <></>,
      key: 'startTimes',
      status: 'secondary',
    };

  const { data: startTimesState } = useSWR<StartTimesState>(
    ['StartTimesState', event?.id],
    async ([, eventId]: [string, string]) => {
      const startTimesState = await apiClient.getEventStartTimesState(eventId);

      return startTimesState;
    },
    { refreshInterval: OneMinuteInMillis, keepPreviousData: true },
  );

  let status: CheckpointEntry['status'] = 'primary';
  let isGunStart = false;
  let weHaveNoStartTimes = false;
  let allStartTimesAreKnown = false;
  const hasBeenStarted = new Date(event.startTime).getTime() <= new Date().getTime();
  if (hasBeenStarted && startTimesState?.isGunStart === true) {
    isGunStart = true;
    status = 'warning';
  }
  if (hasBeenStarted && startTimesState?.numberOfKnownStartTimes === 0) {
    weHaveNoStartTimes = true;
    status = 'danger';
  }
  if (
    hasBeenStarted &&
    startTimesState?.isGunStart === false &&
    startTimesState?.numberOfUnknownStartTimes === 0 &&
    startTimesState?.numberOfKnownStartTimes > 0
  ) {
    allStartTimesAreKnown = true;
    status = 'success';
  }

  return {
    status,
    key: 'startTimes',
    checkpoint: (
      <Checkpoint title="Transponder Start Times">
        <VStack gap={'10px'}>
          <div>
            For accurate predictions, it is essential to have precise start times for each
            transponder. These start times should be promptly forwarded or imported into Racemap
            immediately after being recorded at the raceground.{' '}
          </div>
          {!hasBeenStarted && (
            <div>
              Your event has not started yet, so no start times are expected to be available at this
              moment.
            </div>
          )}
          {allStartTimesAreKnown && (
            <div>
              All start times are known. You are good to go!{' '}
              <span role="img" aria-label="thumbs up">
                👍
              </span>
            </div>
          )}
          {weHaveNoStartTimes && (
            <div>
              We've detected that no start times are available for your transponders. Without start
              times, errors may occur in the prediction algorithm. In live mode, predictions will
              only begin after the second read for each participant, which, depending on the
              placement of your readers, could happen quite late in the race.
            </div>
          )}
          {isGunStart && startTimesState?.gunStartTime != null && (
            <div>
              <div>
                We detected that <strong>{startTimesState?.affectedStarters}</strong> of your
                participants seem to have the same start time. Having a big event with more then 100
                participants mostly lead to long waiting times on the start line. Having the same
                start time for each starter will lead to errors in the prediction allgorithm. It
                might happen that especially reads of the slow participants will be rejected having
                a to slow velocity due to the long time between the wrong start and their next
                possible reading.
              </div>

              <GunStartInformation justify="flex-start" gap="10px">
                <div>Detected Gun Start Time:</div>
                <TooltipWithUTCDatetime
                  datetime={startTimesState?.gunStartTime}
                  hint={'Detected Gun Start Time'}
                >
                  <Timestamp time={startTimesState?.gunStartTime} />
                </TooltipWithUTCDatetime>
              </GunStartInformation>
            </div>
          )}
        </VStack>
      </Checkpoint>
    ),
  };
}

const GunStartInformation = styled(HStack)`
  margin-top: 14px;
  font-size: 150%;
 `;
